import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Users, FileCheck, Building2, CheckCircle, ArrowRight, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { companyInfo, governmentRegistrations } from '../data/mockData';

const GovernmentDefense = () => {
  const relevanceAreas = [
    'Mission-critical electronics depend on trusted engineering decisions',
    'Lower-tier supply-chain visibility remains limited',
    'Provenance-confidence and source-risk decisions drive readiness',
    'Packaging, manufacturing, and sustainment constraints require disciplined technical workflows',
    'Explainable engineering logic is more useful than generic AI claims in high-consequence environments'
  ];

  const partnershipAreas = [
    'Teaming',
    'Government/defense technical collaboration',
    'Engineering partnerships',
    'R&D alignment',
    'Resilience and qualification-focused work',
    'Strategic-industry supply-chain initiatives'
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Government & Defense | SALAR Technologies LLC</title>
        <meta name="description" content="SALAR Technologies federal contracting credentials: UEI, CAGE, DUNS, NAICS, SAM.gov status. Aligned with DoD, DLA, DARPA, USSOCOM, DIU, NSTXL. Public Capability Presentation available." />
        <meta property="og:title" content="SALAR Government, Defense & Partnerships" />
        <meta property="og:description" content="Defense-adjacent, strategic-industry technical engagement. UEI U4TVNBSBUJP3 · CAGE 1AXR9 · SAM Active. DoD/DLA/DARPA/USSOCOM/DIU alignment." />
        <meta property="og:url" content="https://www.salartechnologies.com/government-defense" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/government-defense" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section
        className="text-white py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.7), rgba(10,22,40,0.9)), url('/img/data_center_servers.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="govdef-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="w-20 h-20 mx-auto mb-6 text-slate-300" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Government, Defense, and Partnerships</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Positioned for defense-adjacent, strategic-industry technical engagement and collaboration.
            </p>
          </div>
        </div>
      </section>

      {/* Defense & Research Partners Section - NEW */}
      <section className="py-16 bg-gradient-to-b from-blue-900/40 to-blue-950/60">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">U.S. Defense & Research Alignment</h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              SALAR Technologies aligns with U.S. defense and advanced research priorities, supporting critical missions across defense logistics, research innovation, and supply-chain resilience.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {/* USA Flag */}
              <a
                href="https://www.usa.gov"
                target="_blank"
                rel="noopener noreferrer"
                title="Visit USA.gov"
                data-testid="defense-link-usa"
                className="flex flex-col items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-xl"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden border-4 border-blue-400/40 bg-white flex items-center justify-center p-2 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                  <img 
                    src="/logos/us-flag.jpg"
                    alt="United States Flag"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-center text-blue-100 group-hover:text-white font-semibold text-xs transition-colors">United States</p>
              </a>

              {/* Department of Defense */}
              <a
                href="https://www.defense.gov"
                target="_blank"
                rel="noopener noreferrer"
                title="Visit Defense.gov"
                data-testid="defense-link-dod"
                className="flex flex-col items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-xl"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden border-4 border-cyan-400/40 bg-white flex items-center justify-center p-3 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                  <img 
                    src="/logos/dod-seal.svg"
                    alt="Department of Defense Seal"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-center text-blue-100 group-hover:text-white font-semibold text-xs transition-colors">Department of Defense</p>
              </a>

              {/* Defense Logistics Agency */}
              <a
                href="https://www.dla.mil"
                target="_blank"
                rel="noopener noreferrer"
                title="Visit DLA.mil"
                data-testid="defense-link-dla"
                className="flex flex-col items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-xl"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden border-4 border-blue-400/40 bg-white flex items-center justify-center p-3 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                  <img 
                    src="/logos/dla-seal.svg"
                    alt="Defense Logistics Agency Seal"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-center text-blue-100 group-hover:text-white font-semibold text-xs transition-colors">Defense Logistics Agency</p>
              </a>

              {/* DARPA */}
              <a
                href="https://www.darpa.mil"
                target="_blank"
                rel="noopener noreferrer"
                title="Visit DARPA.mil"
                data-testid="defense-link-darpa"
                className="flex flex-col items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-xl"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden border-4 border-cyan-400/40 bg-white flex items-center justify-center p-3 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                  <img 
                    src="/logos/darpa-seal.svg"
                    alt="DARPA Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-center text-blue-100 group-hover:text-white font-semibold text-xs transition-colors">DARPA</p>
              </a>

              {/* USSOCOM */}
              <a
                href="https://www.socom.mil"
                target="_blank"
                rel="noopener noreferrer"
                title="Visit SOCOM.mil"
                data-testid="defense-link-ussocom"
                className="flex flex-col items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-yellow-300 rounded-xl"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden border-4 border-yellow-400/40 bg-white flex items-center justify-center p-3 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                  <img 
                    src="/logos/socom-seal.svg"
                    alt="US Special Operations Command"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-center text-blue-100 group-hover:text-white font-semibold text-xs transition-colors">USSOCOM</p>
              </a>

              {/* Defense Innovation Unit */}
              <a
                href="https://www.diu.mil"
                target="_blank"
                rel="noopener noreferrer"
                title="Visit DIU.mil"
                data-testid="defense-link-diu"
                className="flex flex-col items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-cyan-300 rounded-xl"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden border-4 border-cyan-400/40 bg-white flex items-center justify-center p-3 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                  <img 
                    src="/logos/diu-logo.svg"
                    alt="Defense Innovation Unit"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-center text-blue-100 group-hover:text-white font-semibold text-xs transition-colors">Defense Innovation Unit</p>
              </a>

              {/* NSTXL */}
              <a
                href="https://nstxl.org"
                target="_blank"
                rel="noopener noreferrer"
                title="Visit NSTXL.org"
                data-testid="defense-link-nstxl"
                className="flex flex-col items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-xl"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden border-4 border-blue-400/40 bg-white flex items-center justify-center p-2 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                  <img 
                    src="/logos/nstxl.png"
                    alt="NSTXL National Security Technology Accelerator"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-center text-blue-100 group-hover:text-white font-semibold text-xs transition-colors">NSTXL</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-blue-950/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-blue-100 leading-relaxed text-center">
              SALAR Technologies LLC is positioned for government-facing, defense-adjacent, and strategic-industry 
              technical engagement in areas including electronics reliability, supply-chain resilience, 
              provenance-confidence decision support, qualification-readiness, and manufacturing stability.
            </p>

            <div className="mt-8 p-6 bg-amber-950/20 border-l-4 border-amber-400 rounded" data-testid="gov-provenance-note">
              <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold mb-2">
                Positioning Note — Provenance &amp; Counterfeit Avoidance
              </p>
              <p className="text-blue-100 text-sm leading-relaxed mb-3">
                For defense and sustainment environments, SALAR supports evidence-based supplier and component assurance
                by connecting BOM data, source-path records, documentation quality, supplier graph risk, inspection
                status, and application criticality into a traceable decision workflow.
              </p>
              <p className="text-blue-100 text-sm leading-relaxed">
                SALAR does not replace authorized-channel sourcing, AS6081-style inspection workflows, laboratory
                counterfeit testing, X-ray, decapsulation, electrical or material analysis, or expert quality judgment.
                SALAR complements those workflows by organizing evidence, scoring provenance confidence, identifying
                source-risk patterns, and preserving decision traceability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why SALAR is Relevant */}
      <section className="py-16 bg-blue-950/20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Why SALAR is Relevant</h2>
          <div className="max-w-4xl mx-auto space-y-4">
            {relevanceAreas.map((area, index) => (
              <Card key={index} className="border-[#1e3a5f]/20">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <CheckCircle className="text-white flex-shrink-0 mt-1" size={24} />
                    <p className="text-blue-100 text-lg">{area}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships and Collaboration */}
      <section className="py-16 bg-blue-900/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-8">
              <Users className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Partnerships and Collaboration
            </h2>
            <p className="text-lg text-blue-100 leading-relaxed mb-8 text-center">
              SALAR welcomes discussions related to:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {partnershipAreas.map((area, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-3 p-4 bg-blue-950/20 rounded-lg border border-[#1e3a5f]/20"
                >
                  <div className="w-2 h-2 bg-blue-950/30 backdrop-blur-sm rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 font-medium">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Business Readiness / Credibility */}
      <section className="py-16 bg-blue-950/30 backdrop-blur-sm text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center mb-8">
              <FileCheck className="w-12 h-12 text-slate-300" />
            </div>
            <h2 className="text-3xl font-bold mb-12 text-center">U.S. Government Procurement Credentials</h2>
            
            {/* Primary Identifiers Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-center text-sm">UEI</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-slate-300 font-mono text-sm">{governmentRegistrations.primary.uei}</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-center text-sm">CAGE Code</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-slate-300 font-mono text-sm">{governmentRegistrations.primary.cageCode}</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-center text-sm">DUNS</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-slate-300 font-mono text-sm">{governmentRegistrations.primary.duns}</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-center text-sm">SAM Status</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <Badge className="bg-green-600 text-white">{governmentRegistrations.primary.samStatus}</Badge>
                  <p className="text-xs text-slate-400 mt-2">Expires: {governmentRegistrations.primary.samExpiration}</p>
                </CardContent>
              </Card>
            </div>

            {/* Company Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Company Information</CardTitle>
                </CardHeader>
                <CardContent className="text-slate-300 space-y-2">
                  <p><strong>Entity Status:</strong> Active Oregon LLC</p>
                  <p><strong>Registry Number:</strong> {companyInfo.registryNumber}</p>
                  <p><strong>Purpose:</strong> {governmentRegistrations.primary.purposeOfRegistration}</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">NAICS Codes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {governmentRegistrations.naicsCodes.map((naics) => (
                    <div key={naics.code} className="flex items-start space-x-2">
                      <Badge variant={naics.primary ? "default" : "outline"} className={naics.primary ? "bg-blue-600" : "border-white/20 text-slate-300"}>
                        {naics.code}
                      </Badge>
                      <p className="text-sm text-slate-300 flex-1">
                        {naics.description}
                        {naics.primary && <span className="text-blue-400 ml-2">(Primary)</span>}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 bg-blue-950/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-[#1e3a5f]">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Positioning Statement</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-blue-100 leading-relaxed">
                  SALAR Technologies LLC is positioned as technically credible, registered, and ready for serious 
                  collaboration and future work. The company frames its capabilities based on engineering discipline, 
                  patent-backed innovation, and public-safe technical positioning rather than unverified claims or 
                  contract awards.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Capability Presentation */}
      <section className="py-16 bg-gradient-to-b from-blue-950/40 to-blue-900/40" data-testid="capability-presentation-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-blue-500/40 bg-blue-900/30 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-700 rounded flex items-center justify-center border-2 border-blue-400/30 flex-shrink-0">
                    <FileText className="text-white" size={26} />
                  </div>
                  <div>
                    <Badge variant="outline" className="border-blue-400 text-blue-200 mb-2">
                      Public Capability Briefing · UNCLASSIFIED
                    </Badge>
                    <CardTitle className="text-2xl text-white">SALAR Capability Presentation</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-blue-100 leading-relaxed mb-6">
                  Public, unclassified capability presentation prepared for government program offices, prime
                  contractors, and strategic-industry partners. Summarizes SALAR's eight patent-backed capability
                  lanes, registration credentials, alignment with DoD / DLA / DARPA / DIU / USSOCOM mission
                  priorities, and engagement pathways for teaming, R&amp;D collaboration, and supply-chain
                  resilience initiatives.
                </p>
                <a
                  href="/SALAR_Public_Website_Capability_Presentation.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="capability-presentation-download"
                >
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Download className="mr-2" size={18} />
                    Download Capability Presentation (PDF · 300 KB)
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 text-white"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.85), rgba(10,22,40,0.95)), url('/img/ai_fabric_hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="govdef-cta"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Explore Collaboration?</h2>
            <p className="text-lg text-slate-300 mb-8">
              Contact SALAR Technologies LLC to discuss government, defense, partnership, or technical collaboration opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#0a1628] hover:bg-slate-100 font-semibold" data-testid="cta-start-conversation">
                  Start a Conversation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/resources">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0a1628]" data-testid="cta-view-resources">
                  View Resources
                </Button>
              </Link>
              <Link to="/salar-resilience-terrain">
                <Button size="lg" variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]" data-testid="cta-resilience-terrain">
                  Resilience Terrain Explorer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GovernmentDefense;
